import React, { useMemo, useState } from 'react';
import {
  AnalysisModel, Baseline, Metric, MissingData, PlayerModel, RoundFilter, SgCell,
  fmtSigned, fmtToPar, strokesGainedByHole, strokesGainedByParType,
} from '../../utils/majorAnalysis';
import { divergingColor, inkOn } from '../../utils/chartPalette';
import { HolesAsk, ParsAsk } from './DataNeeded';
import { Empty, PlayerCell, RankCell, Seg, SortTh, fmt1, sortRows, useSort } from './shared';

interface Props {
  model: AnalysisModel;
  players: PlayerModel[];
  metric: Metric;
  filter: RoundFilter;
  missing: MissingData;
  onOpenRound: (roundId: number) => void;
}

const FRONT = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const BACK = [10, 11, 12, 13, 14, 15, 16, 17, 18];

const SgTd: React.FC<{ cell: SgCell | null; mode: 'total' | 'avg'; scale: number; title: string; strong?: boolean }> = ({ cell, mode, scale, title, strong }) => {
  if (!cell) return <td className="c ma-nil">·</td>;
  const v = mode === 'total' ? cell.sum : cell.sum / cell.n;
  const bg = divergingColor(v, scale);
  return (
    <td className={`c ma-heat${strong ? ' ma-strong' : ''}`} style={{ background: bg, color: inkOn(bg) }}
      title={`${title}: ${fmtSigned(v)} (${cell.n} hole${cell.n === 1 ? '' : 's'} played)`}>
      {fmtSigned(v)}
    </td>
  );
};

const ScaleLegend: React.FC<{ scale: number }> = ({ scale }) => (
  <div className="ma-scale" aria-hidden>
    <span>Lost strokes</span>
    {[-1, -0.66, -0.33, 0, 0.33, 0.66, 1].map((t) => (
      <i key={t} style={{ background: divergingColor(t * scale, scale) }} />
    ))}
    <span>Gained strokes</span>
  </div>
);

const HoleByHole: React.FC<Props> = ({ model, players, metric, filter, missing, onOpenRound }) => {
  const [course, setCourse] = useState<string>(filter.course !== 'all' ? filter.course : model.courses[0]?.key ?? 'all');
  const [baseline, setBaseline] = useState<Baseline>('field');
  const [mode, setMode] = useState<'total' | 'avg'>('total');
  const { sort, toggle } = useSort();
  const info = model.courses.find((c) => c.key === course);

  const grid = useMemo(
    () => (course === 'all' ? null : strokesGainedByHole(model, course, baseline, metric, { round: filter.round, course })),
    [model, course, baseline, metric, filter.round],
  );
  const parType = useMemo(
    () => (course === 'all' ? strokesGainedByParType(model, baseline, metric, { round: filter.round, course: 'all' }) : null),
    [model, course, baseline, metric, filter.round],
  );

  const controls = (
    <div className="ma-tab-bar">
      <label className="ma-field">
        <span>Course</span>
        <select value={course} onChange={(e) => setCourse(e.target.value)}>
          {model.courses.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          <option value="all">All courses (by par type)</option>
        </select>
      </label>
      <Seg label="Baseline" value={baseline} onChange={setBaseline} options={[
        { value: 'field', label: 'vs Field', title: 'The field’s average on the hole minus your score' },
        { value: 'par', label: 'vs Par', title: 'Par minus your score' },
      ]} />
      <Seg label="Show" value={mode} onChange={setMode} options={[
        { value: 'total', label: 'Total', title: 'Summed over every time the hole was played' },
        { value: 'avg', label: course === 'all' ? 'Per hole' : 'Per round', title: 'Average each time it was played' },
      ]} />
    </div>
  );

  const explain = (
    <p className="ma-lede">
      Strokes gained on a hole = {baseline === 'field' ? 'the field’s average on that hole (everyone in this major who played it)' : 'par'} −
      your score{metric === 'net' ? ' (net: the round’s handicap spread evenly over its holes — stroke index isn’t stored)' : ' (gross)'}.
      Positive (blue) is better. {mode === 'total' ? 'Summed over every round you played it.' : 'Averaged per time played.'}
    </p>
  );

  const coursesMatches = (key: string) => {
    const label = model.courses.find((c) => c.key === key)?.label;
    return missing.holeScores.filter((m) => m.course === label);
  };

  // ── All courses: par 3 / 4 / 5 and nines ──────────────────────────────────
  if (course === 'all' && parType) {
    const byId = new Map(parType.map((r) => [r.player_id, r]));
    const anyData = parType.some((r) => r.total);
    const cols: { key: keyof typeof parType[number]; label: string }[] = [
      { key: 'par3', label: 'Par 3s' }, { key: 'par4', label: 'Par 4s' }, { key: 'par5', label: 'Par 5s' },
      { key: 'front', label: 'Front 9' }, { key: 'back', label: 'Back 9' }, { key: 'total', label: 'Total' },
    ];
    const cellVal = (c: SgCell | null) => (c ? (mode === 'total' ? c.sum : c.sum / c.n) : null);
    const scale = Math.max(0.5, ...parType.flatMap((r) => cols.slice(0, 5).map((c) => Math.abs(cellVal(r[c.key] as SgCell | null) ?? 0))));
    const sorted = sortRows(players, sort, (p) => p.order, (p, key) => cellVal((byId.get(p.player_id)?.[key as 'par3'] ?? null) as SgCell | null));
    return (
      <div className="ma-tab">
        {controls}
        {!anyData ? (
          <Empty title={baseline === 'par' && missing.teeboxesWithoutPars.length ? 'Strokes gained vs par needs hole scores and hole pars.' : 'No hole-by-hole scores in this major yet.'}>
            <HolesAsk matches={missing.holeScores} onOpenRound={onOpenRound} unlocks="strokes gained on every hole" />
            <ParsAsk missing={missing} />
          </Empty>
        ) : (
          <>
            {explain}
            <div className="ma-scroll">
              <table className="tbl tbl--sm ma-table ma-table--sticky">
                <thead>
                  <tr>
                    <SortTh label="Rank" sortKey="rank" sort={sort} onSort={toggle} className="c ma-stick-1" />
                    <th className="ma-stick-2">Player</th>
                    {cols.map((c) => <SortTh key={c.key} label={c.label} sortKey={c.key} sort={sort} onSort={toggle} firstDir={-1} className="c" />)}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p) => {
                    const r = byId.get(p.player_id);
                    return (
                      <tr key={p.player_id}>
                        <RankCell p={p} all={model.players} />
                        <PlayerCell p={p} />
                        {cols.map((c) => <SgTd key={c.key} cell={(r?.[c.key] ?? null) as SgCell | null} mode={mode} scale={scale} title={`${p.first_name}, ${c.label}`} strong={c.key === 'total'} />)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ScaleLegend scale={scale} />
            {missing.teeboxesWithoutPars.length > 0 && (
              <p className="ma-foot">Par 3 / 4 / 5 columns leave out holes with no par: {missing.teeboxesWithoutPars.map((t) => t.label).join(', ')}.</p>
            )}
          </>
        )}
      </div>
    );
  }

  // ── One course: every hole ────────────────────────────────────────────────
  if (!grid || !info) return <div className="ma-tab">{controls}</div>;
  const played = grid.holes.filter((h) => h.n > 0).map((h) => h.hole);
  const front = FRONT.filter((h) => played.includes(h) || (played.length === 0 && info.hole_pars[h - 1] != null));
  const back = BACK.filter((h) => played.includes(h));
  const anyData = played.length > 0;
  const noPars = baseline === 'par' && !info.hasHolePars;
  const byId = new Map(grid.rows.map((r) => [r.player_id, r]));
  const cellVal = (c: SgCell | null) => (c ? (mode === 'total' ? c.sum : c.sum / c.n) : null);
  const scale = Math.max(0.5, ...grid.rows.flatMap((r) => r.holes.map((c) => Math.abs(cellVal(c) ?? 0))));
  const valueFor = (p: PlayerModel, key: string) => {
    const r = byId.get(p.player_id);
    if (!r) return null;
    if (key === 'out') return cellVal(r.out);
    if (key === 'in') return cellVal(r.in);
    if (key === 'total') return cellVal(r.total);
    return cellVal(r.holes[Number(key.slice(1)) - 1]);
  };
  const sorted = sortRows(players, sort, (p) => p.order, valueFor);
  const holeCols = [...front.map((h) => ({ key: `h${h}`, hole: h })), ...(front.length ? [{ key: 'out', hole: 0 }] : []), ...back.map((h) => ({ key: `h${h}`, hole: h })), ...(back.length ? [{ key: 'in', hole: 0 }] : [])];

  return (
    <div className="ma-tab">
      {controls}
      {!anyData ? (
        <Empty title={`No hole-by-hole scores on ${info.label} yet.`}>
          <HolesAsk matches={coursesMatches(course)} onOpenRound={onOpenRound} unlocks="strokes gained on every hole of this course" />
          <ParsAsk missing={missing} only={[course]} />
        </Empty>
      ) : noPars ? (
        <Empty title={`Strokes gained vs par needs the hole pars for ${info.label}.`}>
          <ParsAsk missing={missing} only={[course]} />
        </Empty>
      ) : (
        <>
          {explain}
          <div className="ma-scroll">
            <table className="tbl tbl--sm ma-table ma-table--sticky ma-holes">
              <thead>
                <tr>
                  <SortTh label="Rank" sortKey="rank" sort={sort} onSort={toggle} className="c ma-stick-1" />
                  <th className="ma-stick-2">Hole</th>
                  {holeCols.map((c) => (
                    <SortTh key={c.key} sortKey={c.key} sort={sort} onSort={toggle} firstDir={-1}
                      className={`c${c.hole === 0 ? ' ma-col-end' : ''}`} label={c.hole === 0 ? (c.key === 'out' ? 'Out' : 'In') : c.hole} />
                  ))}
                  <SortTh label="Total" sortKey="total" sort={sort} onSort={toggle} firstDir={-1} className="c ma-col-end" />
                </tr>
                <tr className="ma-subhead">
                  <th colSpan={2} className="ma-stick-1">Par</th>
                  {holeCols.map((c) => <th key={c.key} className={`c${c.hole === 0 ? ' ma-col-end' : ''}`}>{c.hole === 0 ? '' : info.hole_pars[c.hole - 1] ?? '–'}</th>)}
                  <th className="c ma-col-end">{info.par ?? ''}</th>
                </tr>
                <tr className="ma-subhead">
                  <th colSpan={2} className="ma-stick-1" title="The field’s average gross score on the hole, vs par when the par is known">Field avg</th>
                  {holeCols.map((c) => {
                    const h = c.hole ? grid.holes[c.hole - 1] : null;
                    return <th key={c.key} className={`c${c.hole === 0 ? ' ma-col-end' : ''}`}>{h ? (h.avgToPar != null ? fmtToPar(h.avgToPar) : fmt1(h.avg)) : ''}</th>;
                  })}
                  <th className="c ma-col-end" />
                </tr>
                <tr className="ma-subhead">
                  <th colSpan={2} className="ma-stick-1" title="1 = hardest hole on this course for this field">Difficulty</th>
                  {holeCols.map((c) => <th key={c.key} className={`c${c.hole === 0 ? ' ma-col-end' : ''}`}>{c.hole ? grid.holes[c.hole - 1].difficulty ?? '' : ''}</th>)}
                  <th className="c ma-col-end" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => {
                  const r = byId.get(p.player_id);
                  return (
                    <tr key={p.player_id}>
                      <RankCell p={p} all={model.players} />
                      <PlayerCell p={p} />
                      {holeCols.map((c) => {
                        const cell = !r ? null : c.key === 'out' ? r.out : c.key === 'in' ? r.in : r.holes[c.hole - 1];
                        return <SgTd key={c.key} cell={cell} mode={mode} scale={scale} strong={c.hole === 0} title={`${p.first_name}, ${c.hole ? `hole ${c.hole}` : c.key}`} />;
                      })}
                      <SgTd cell={r?.total ?? null} mode={mode} scale={scale} strong title={`${p.first_name}, total`} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ScaleLegend scale={scale} />
          {coursesMatches(course).length > 0 && (
            <HolesAsk matches={coursesMatches(course)} onOpenRound={onOpenRound} unlocks="the missing rounds on this course" />
          )}
        </>
      )}
    </div>
  );
};

export default HoleByHole;
